// Loan calculation utilities

/*export const calculateEMI = (principal, annualRate, tenureMonths) => {
  // Validate inputs
  if (!principal || principal <= 0) return 0;
  if (!annualRate || annualRate <= 0) return 0;
  if (!tenureMonths || tenureMonths <= 0) return 0;
  
  // Convert annual rate to monthly rate (divide by 12 and convert percentage to decimal)
  const monthlyRate = annualRate / (12 * 100);
  
  // Handle edge case where interest rate is 0
  if (monthlyRate === 0) {
    return Math.round(principal / tenureMonths);
  }
  
  // EMI Formula: [P x R x (1+R)^N] / [(1+R)^N-1]
  // Where P = Principal, R = Monthly Interest Rate, N = Number of months
  const onePlusR = 1 + monthlyRate;
  const onePlusRPowerN = Math.pow(onePlusR, tenureMonths);
  
  const emi = (principal * monthlyRate * onePlusRPowerN) / (onePlusRPowerN - 1);
  
  return Math.round(emi);
};*/
export const calculateEMI = (principal, annualRate, tenureMonths) => {
  // Validate inputs
  if (!principal || principal <= 0) return null;
  if (!annualRate || annualRate < 0) return null;
  if (!tenureMonths || tenureMonths <= 0) return null;

  // Convert annual rate to monthly
  const monthlyRate = annualRate / 12 / 100;

  // If interest rate is 0
  if (monthlyRate === 0) {
    const emi = principal / tenureMonths;
    return {
      emi: Math.round(emi),
      totalPayment: Math.round(principal),
      totalInterest: 0,
    };
  }

  // EMI Formula
  const emi =
    (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths)) /
    (Math.pow(1 + monthlyRate, tenureMonths) - 1);

  const totalPayment = emi * tenureMonths;
  const totalInterest = totalPayment - principal;

  return {
    emi: Math.round(emi),
    totalPayment: Math.round(totalPayment),
    totalInterest: Math.round(totalInterest),
  };
};

// Calculate age from date of birth
export const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Get fixed interest rate based on loan type
export const getInterestRate = (loanType) => {
  switch (loanType) {
    case 'Personal':
      return 11.5; // Personal loan: 11.5%
    case 'Business':
      return 11; // Business loan: 11%
    case 'Home':
      return 8.5; // Home loan: 8.5%
    case 'Car':
      return 9.5; // Car loan: 9.5%
    case 'Loan Against Property':
      return 9.5; // Loan Against Property: 9.5%
    case 'Mortgage':
      return 9.5; // Mortgage loan: 9.5%
    default:
      return 8.5; // Default to home loan rate
  }
};

// Calculate EMI capacity based on income
export const calculateEMICapacity = (customerType, monthlySalary = 0, netProfit = 0, otherIncome = 0, existingEMI = 0) => {
  const mainIncome = customerType === 'Business Owner' 
    ? (parseFloat(netProfit) / 12 || 0) 
    : (parseFloat(monthlySalary) || 0);
    
  const other = parseFloat(otherIncome) || 0;
  const emi = parseFloat(existingEMI) || 0;
  
  if (mainIncome <= 0 && other <= 0) return 0;
  
  if (customerType === 'Business Owner') {
    // Business: (Annual Net Profit * 80% + Monthly Rental Income * 50%) minus EMI
    const businessEMICapacity = (parseFloat(netProfit) * 0.80 / 12) + (parseFloat(otherIncome) * 0.50);
    return Math.max(0, businessEMICapacity - emi);
  }
  
  // Salaried: (Monthly Salary * 65% + Monthly Rental Income * 50%) minus EMI
  const salaryEMICapacity = (mainIncome * 0.65) + (other * 0.50);
  return Math.max(0, salaryEMICapacity - emi);
};

// Calculate eligible loan amount based on EMI capacity using reverse EMI formula
export const calculateEligibleLoanFromEMI = (emiCapacity, annualRate, years) => {
  if (emiCapacity <= 0) return 0;
  
  const monthlyRate = annualRate / 12 / 100;
  const months = years * 12;
  
  if (monthlyRate === 0) {
    return emiCapacity * months;
  }
  
  const numerator = (Math.pow(1 + monthlyRate, months) - 1);
  const denominator = monthlyRate * Math.pow(1 + monthlyRate, months);
  const factor = numerator / denominator;
  
  return emiCapacity * factor;
};

// Calculate maximum tenure based on customer age and loan type
export const getMaxTenure = (loanType, customerType, customerAge) => {
  // Validate inputs
  if (!loanType || !customerType || !customerAge) return 12; // Default 1 year
  
  // Maximum loan tenures by type (in years)
  const maxTenureByType = {
    'Business': 5,
    'Personal': 3,
    'Home': 20,
    'Car': 5,
    'Loan Against Property': 15,
    'Mortgage': 15
  };
  
  // Maximum age limits based on customer type
  const maxAge = customerType === 'Salaried' ? 62 : 75;
  
  // Calculate maximum years based on age (customer should not exceed max age at loan completion)
  const maxYearsByAge = Math.max(0, maxAge - customerAge);
  
  // Get maximum years for loan type
  const maxYearsByType = maxTenureByType[loanType] || 4;
  
  // Return the minimum of the two constraints (in months)
  // Ensure minimum tenure is at least 12 months
  const maxTenureMonths = Math.min(maxYearsByAge, maxYearsByType) * 12;
  return Math.max(12, maxTenureMonths);
};

export const getLoanDetails = (loanType, customerType, loanAmount, customerAge, customerDob, netProfit = 0, otherIncome = 0, existingEMI = 0, monthlySalary = 0) => {
  // Validate required inputs
  if (!loanType || !customerType) {
    return null;
  }
  
  // Calculate customer age if not provided
  const age = customerAge || calculateAge(customerDob);
  if (!age || age < 18) {
    return null; // Invalid age
  }
  
  // Calculate maximum tenure based on age and loan type
  const maxTenureMonths = getMaxTenure(loanType, customerType, age);
  
  // Set optimal tenure based on loan type
  const optimalTenure = {
    'Personal': 36, // 3 years
    'Business': 60, // 5 years
    'Home': 240, // 20 years
    'Car': 60, // 5 years
    'Loan Against Property': 180, // 15 years
    'Mortgage': 180 // 15 years
  };
  
  // Use the minimum of optimal tenure and maximum allowed tenure
  const tenure = Math.min(maxTenureMonths, optimalTenure[loanType] || 60);
  
  // Ensure tenure is at least 12 months
  const finalTenure = Math.max(12, tenure);
  const tenureYears = finalTenure / 12;
  
  // Get interest rate for the loan type
  const interestRate = getInterestRate(loanType);
  
  // Calculate EMI capacity and eligible loan amount based on income
  const emiCapacity = calculateEMICapacity(customerType, monthlySalary, netProfit, otherIncome, existingEMI);
  
  // Calculate eligible loan amount from EMI capacity
  const approvedAmount = Math.round(calculateEligibleLoanFromEMI(emiCapacity, interestRate, tenureYears));
  
  // Calculate EMI using the correct formula
  const emiData = calculateEMI(approvedAmount, interestRate, finalTenure);
  
  // Handle EMI data properly
  if (!emiData || approvedAmount <= 0) {
    return {
      requestedAmount: parseFloat(loanAmount) || 0,
      approvedAmount: 0,
      emiCapacity: 0,
      interestRate,
      tenure: finalTenure,
      tenureYears,
      maxTenureMonths,
      maxTenureYears: Math.round((maxTenureMonths / 12) * 100) / 100,
      emi: 0,
      totalPayment: 0,
      totalInterest: 0,
      monthlyInterest: 0,
      principalComponent: 0,
      interestComponent: 0,
      customerAge: age,
      isEligible: false
    };
  }
  
  // Extract values from emiData object
  const emi = emiData.emi;
  const totalPayment = emiData.totalPayment;
  const totalInterest = emiData.totalInterest;
  
  // Calculate monthly interest component
  const monthlyInterest = finalTenure > 0 ? Math.round(totalInterest / finalTenure) : 0;
  
  // Calculate precise tenure in years
  const maxTenureYears = Math.round((maxTenureMonths / 12) * 100) / 100;
  
  return {
    requestedAmount: parseFloat(loanAmount) || 0,
    approvedAmount,
    emiCapacity: Math.round(emiCapacity),
    interestRate,
    tenure: finalTenure,
    tenureYears,
    maxTenureMonths,
    maxTenureYears,
    emi,
    totalPayment: Math.round(totalPayment),
    totalInterest: Math.round(totalInterest),
    monthlyInterest,
    principalComponent: Math.round(approvedAmount / finalTenure),
    interestComponent: monthlyInterest,
    customerAge: age,
    isEligible: approvedAmount > 0 && age >= 21 && age <= 65
  };
};

export const formatCurrency = (amount) => {
  if (!amount || isNaN(amount)) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Utility function to format numbers with commas
export const formatNumber = (number) => {
  if (!number || isNaN(number)) return '0';
  return new Intl.NumberFormat('en-IN').format(number);
};

// Function to validate EMI calculation with example
export const validateEMICalculation = () => {
  // Example: ₹10,000 at 6% annual interest for 36 months
  // Expected EMI ≈ ₹303.87
  const principal = 10000;
  const annualRate = 6;
  const tenureMonths = 36;
  
  const emiData = calculateEMI(principal, annualRate, tenureMonths);
  const calculatedEMI = emiData ? emiData.emi : 0;
  const expectedEMI = 304; // Rounded expected value
  
  console.log('EMI Validation:');
  console.log(`Principal: ₹${formatNumber(principal)}`);
  console.log(`Annual Rate: ${annualRate}%`);
  console.log(`Tenure: ${tenureMonths} months`);
  console.log(`Calculated EMI: ₹${calculatedEMI}`);
  console.log(`Expected EMI: ₹${expectedEMI}`);
  console.log(`Validation: ${Math.abs(calculatedEMI - expectedEMI) <= 1 ? 'PASSED' : 'FAILED'}`);
  
  return Math.abs(calculatedEMI - expectedEMI) <= 1;
};

// Function to get loan eligibility based on age
export const getLoanEligibility = (customerType, age) => {
  const maxAge = 55; // Maximum age for loan eligibility (standardized)
  const minAge = 21; // Minimum age for loan eligibility (standardized)
  
  if (age < minAge) {
    return {
      eligible: false,
      reason: `Minimum age requirement is ${minAge} years`
    };
  }
  
  if (age > maxAge) {
    return {
      eligible: false,
      reason: `Maximum age limit for loan is ${maxAge} years`
    };
  }
  
  return {
    eligible: true,
    remainingYears: maxAge - age,
    reason: `✓ Eligible for loan`
  };
};